import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { UserService } from '@core/services/user.service';
import { ApiResponse, UserSettings } from '@core/types';
import {
  User,
  UserCreate,
  UserUpdate,
  UserGridConfig,
  UserPaginationResult,
} from '@core/types/user.type';

@Injectable({ providedIn: 'root' })
export class UserQuery {
  private readonly service = inject(UserService);
  private readonly queryClient = inject(QueryClient);

  useUsersGrid(config: () => UserGridConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.users.grid(c),
        queryFn: () => lastValueFrom(this.service.getGrid(c)),
        select: (res: ApiResponse<UserPaginationResult>) =>
          res.data ?? { list: [], totalRecord: 0, pageCount: 0 },
        staleTime: environment.staleTime,
      };
    });
  }

  useUser(id: () => string | null) {
    return injectQuery(() => {
      const idVal = id();
      return {
        queryKey: QUERY_KEYS.users.detail(idVal ?? ''),
        queryFn: () => lastValueFrom(this.service.get(idVal!)),
        select: (res: ApiResponse<User>) => res.data,
        enabled: !!idVal,
      };
    });
  }

  useCreateUser() {
    return injectMutation(() => ({
      mutationFn: (data: UserCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all }),
    }));
  }

  useUpdateUser() {
    return injectMutation(() => ({
      mutationFn: (data: UserUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      },
    }));
  }

  useDeleteUser() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all }),
    }));
  }

  useSettings() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.user.settings(),
      queryFn: () => lastValueFrom(this.service.getSettings()),
      select: (res: ApiResponse<UserSettings>) => res.data,
      staleTime: environment.staleTime,
    }));
  }

  useUpdateSettings() {
    return injectMutation(() => ({
      mutationFn: (data: UserSettings) => lastValueFrom(this.service.updateSettings(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user.settings() });
      },
    }));
  }
}
